import { parseFiniteNumber } from '@/lib/number';

type CleanerApiEnvelope = Record<string, unknown>;

export type CleanerJobBlock = {
  id: string;
  sourceCode: string;
  area: string;
  label: string;
  status: 'planned' | 'completed' | 'skipped' | 'cancelled';
  points: number;
  requiredPhotos: number;
  completionNotes: string | null;
  completedAt: string | null;
};

export type CleanerVisitJob = {
  id: string;
  siteId: string;
  siteName: string;
  scheduledFor: string;
  status: 'assigned' | 'completed' | 'cancelled' | 'approved';
  totalPoints: number;
  assignedUserId: string | null;
  blocks: CleanerJobBlock[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown, keys: string[]): string | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

function readNumber(value: unknown, keys: string[]): number | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const parsed = parseFiniteNumber(value[key]);
    if (parsed != null) {
      return parsed;
    }
  }
  return null;
}

function readArray(value: unknown, keys: string[]): unknown[] {
  if (!isRecord(value)) return [];
  for (const key of keys) {
    const candidate = value[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }
  return [];
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractError(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (isRecord(payload)) {
    const message = readString(payload, ['message', 'error', 'details']);
    if (message) {
      return message;
    }
  }

  return fallback;
}

async function requestJson<T>(
  path: string,
  sessionToken: string | null,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');

  if (sessionToken) {
    headers.set('Authorization', `Bearer ${sessionToken}`);
  }

  const response = await fetch(path, {
    ...init,
    headers,
  });

  const rawText = await response.text();
  const parsed = rawText ? safeParse(rawText) : null;

  if (!response.ok) {
    throw new Error(extractError(parsed, `${response.status} ${response.statusText}`));
  }

  return parsed as T;
}

function parseBlock(value: unknown): CleanerJobBlock | null {
  if (!isRecord(value)) return null;

  return {
    id: readString(value, ['id']) ?? '',
    sourceCode: readString(value, ['source_code', 'sourceCode']) ?? '—',
    area: readString(value, ['area']) ?? '—',
    label: readString(value, ['label']) ?? '—',
    status: (readString(value, ['status']) as CleanerJobBlock['status'] | null) ?? 'planned',
    points: readNumber(value, ['points']) ?? 0,
    requiredPhotos: readNumber(value, ['required_photos', 'requiredPhotos']) ?? 0,
    completionNotes: readString(value, ['completion_notes', 'completionNotes']),
    completedAt: readString(value, ['completed_at', 'completedAt']),
  };
}

function parseVisit(value: unknown): CleanerVisitJob | null {
  if (!isRecord(value)) return null;

  const blocks = readArray(value, ['blocks', 'job_blocks'])
    .map(parseBlock)
    .filter((block): block is CleanerJobBlock => block !== null);

  return {
    id: readString(value, ['id']) ?? '',
    siteId: readString(value, ['site_id', 'siteId']) ?? '',
    siteName: readString(value, ['site_name', 'siteName']) ?? 'Unknown site',
    scheduledFor: readString(value, ['scheduled_for', 'scheduledFor']) ?? '',
    status: (readString(value, ['status']) as CleanerVisitJob['status'] | null) ?? 'assigned',
    totalPoints: readNumber(value, ['total_points', 'totalPoints']) ?? 0,
    assignedUserId: readString(value, ['assigned_user_id', 'assignedUserId']),
    blocks,
  };
}

export async function loadCleanerJobs(sessionToken: string | null): Promise<CleanerVisitJob[]> {
  const response = await requestJson<CleanerApiEnvelope>(
    '/api/ops/cleaner/jobs',
    sessionToken,
    { method: 'GET' },
  );

  return readArray(response, ['jobs', 'visits'])
    .map(parseVisit)
    .filter((job): job is CleanerVisitJob => job !== null);
}

export async function updateCleanerJobBlockStatus(
  input: {
    jobBlockId: string;
    status: CleanerJobBlock['status'];
    completionNotes?: string | null;
  },
  sessionToken: string | null,
): Promise<void> {
  await requestJson<CleanerApiEnvelope>(
    '/api/ops/cleaner/job-blocks/status',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify({
        jobBlockId: input.jobBlockId,
        status: input.status,
        completionNotes: input.completionNotes ?? null,
      }),
    },
  );
}
